<template>
    <v-dialog :value="dialog" v-stack="{ open: dialog, close: closeDialog }" fullscreen
        transition="dialog-bottom-transition" @input="onInput">
        <div class="camera-stage">

            <v-btn icon class="glass-btn close-button" @click="closeDialog">
                <v-icon color="#FFFFFF">mdi-close</v-icon>
            </v-btn>

            <div v-if="isRecording" class="recording-time" :class="{ 'recording-time--warning': isNearLimit }">
                <span class="rec-dot"></span>
                {{ formatTime(recordingTime) }} / {{ formatTime(maxSeconds) }}
            </div>

            <div v-if="isRecording" class="recording-progress">
                <span class="recording-progress-bar" :class="{ 'recording-progress-bar--warning': isNearLimit }"
                    :style="{ width: `${progress}%` }"></span>
            </div>

            <button-tooltip bottom :tooltip="$t('common.switch_camera')">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-show="!isPlayback && !isRecording" icon class="glass-btn switch-button" v-on="on"
                        v-bind="attrs" @click="switchCamera">
                        <v-icon color="#FFFFFF" size="20">mdi-camera-flip</v-icon>
                    </v-btn>
                </template>
            </button-tooltip>

            <video v-show="!isPlayback" ref="video" autoplay muted class="video-fullscreen video-fullscreen--preview"
                playsinline />

            <video v-show="isPlayback" ref="playback" autoplay controls playsinline
                class="video-fullscreen video-fullscreen--playback" />

            <div class="camera-controls">
                <template v-if="isPlayback">
                    <v-btn icon class="glass-btn action-btn" @click="discardVideo">
                        <v-icon color="#FFFFFF" size="22">$delete</v-icon>
                    </v-btn>
                    <v-btn icon class="action-btn action-btn--primary" :loading="loadingSendToCoach"
                        @click="sendToCoach">
                        <template v-slot:loader>
                            <v-progress-circular indeterminate size="22" width="2" color="#0B0E13" />
                        </template>
                        <v-icon color="#0B0E13" size="24">mdi-send</v-icon>
                    </v-btn>
                    <v-btn icon class="glass-btn action-btn" @click="shareVideo">
                        <v-icon color="#FFFFFF" size="22">{{ isWeb ? 'mdi-download' : 'mdi-share-variant' }}</v-icon>
                    </v-btn>
                </template>

                <v-btn v-else icon class="shutter" :class="{ 'shutter--recording': isRecording }"
                    @click="toggleRecording">
                    <span class="shutter-core"></span>
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { db, FieldValue, storageRef } from '@/utils/Firebase'
import { sendMessage } from '@/utils/Messages'
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { randomString } from "@/utils/Common";
import { sanitizeFilename } from "@/utils/Export";
import { Device } from "@/utils/Capacitor";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import {
    supportedVideoMimeType,
    mediaExtension,
    canRecordMedia,
    DEFAULT_VIDEO_MIME_TYPE,
    VIDEO_CONSTRAINTS,
    VIDEO_RECORDER_OPTIONS,
    MAX_VIDEO_SECONDS,
    VIDEO_WARNING_SECONDS
} from "@/utils/Recorder";

export default {
    components: { ButtonTooltip },
    props: {
        exercise: {
            type: Object,
            default: null
        },
        assignedRoutineId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            mediaRecorder: null,
            isRecording: false,
            recordedChunks: [],
            videoMimeType: DEFAULT_VIDEO_MIME_TYPE,
            isPlayback: false,
            stream: null,
            folderPath: 'my_cache_videos',
            recordingTime: 0,
            recordingInterval: null,
            isWeb: false,
            loadingSendToCoach: false,
            facingMode: 'environment',
            maxSeconds: MAX_VIDEO_SECONDS,
        };
    },
    computed: {
        studentId(vm = this) {
            return vm.currentUser?.uid || null
        },
        coachId(vm = this) {
            return vm.account?.studentSubscription?.coachId || null
        },
        isNearLimit(vm = this) {
            return vm.maxSeconds - vm.recordingTime <= VIDEO_WARNING_SECONDS
        },
        progress(vm = this) {
            return Math.min(100, (vm.recordingTime / vm.maxSeconds) * 100)
        },
    },
    async mounted() {
        const vm = this;
        vm.isWeb = await Device.isWeb();
    },
    methods: {
        show() {
            const vm = this;
            vm.resetRecording();
            vm.dialog = true;
            vm.initializeCamera()
        },
        hide() {
            const vm = this;
            vm.dialog = false;
            vm.stopStream()
        },
        onInput(val) {
            if (!val) this.closeDialog()
        },
        async initializeCamera() {
            const vm = this;
            if (!canRecordMedia()) return vm.toast(vm.$t('common.camera_access_error'), 'error');
            try {
                vm.stream = await navigator.mediaDevices.getUserMedia({
                    video: { ...VIDEO_CONSTRAINTS, facingMode: { ideal: vm.facingMode } },
                    audio: true,
                });
                const videoElement = vm.$refs.video;
                videoElement.srcObject = vm.stream;
            } catch (err) {
                console.error("Error al acceder a la cámara: ", err);
                vm.toast(vm.$t('common.camera_access_error'), 'error');
            }
        },
        async switchCamera() {
            const vm = this;
            if (vm.isRecording) return
            vm.facingMode = vm.facingMode === 'environment' ? 'user' : 'environment'
            vm.stopStream()
            await vm.initializeCamera()
        },
        async startRecording() {
            const vm = this;
            if (!vm.stream) return;

            try {
                const mimeType = supportedVideoMimeType();
                const options = { ...VIDEO_RECORDER_OPTIONS };
                if (mimeType) options.mimeType = mimeType;

                vm.mediaRecorder = new MediaRecorder(vm.stream, options);
                vm.videoMimeType = vm.mediaRecorder.mimeType || mimeType || DEFAULT_VIDEO_MIME_TYPE;
            } catch (e) {
                return vm.toast(vm.$t('common.video_record_error'), "error");
            }

            vm.mediaRecorder.onerror = () => {
                vm.cleanupRecording();
                vm.toast(vm.$t('common.video_record_error'), "error");
            };

            vm.mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    vm.recordedChunks.push(event.data);
                }
            };

            vm.mediaRecorder.onstart = () => {
                vm.isRecording = true;
                vm.recordingTime = 0;
                vm.recordingInterval = setInterval(() => {
                    vm.recordingTime++;
                    if (vm.recordingTime >= vm.maxSeconds) vm.stopRecording()
                }, 1000);
            };

            vm.mediaRecorder.onstop = () => {
                vm.isRecording = false;
                clearInterval(vm.recordingInterval);
                vm.recordingInterval = null;

                if (vm.recordedChunks.length > 0) {
                    const playbackElement = vm.$refs.playback;
                    playbackElement.src = URL.createObjectURL(vm.recordedBlob());
                    vm.isPlayback = true;
                    vm.stopStream();
                } else {
                    vm.toast(vm.$t('common.video_record_error'), "error");
                }
            };

            vm.mediaRecorder.start();
        },
        stopRecording() {
            if (this.mediaRecorder && this.isRecording) this.mediaRecorder.stop();
        },
        recordedBlob() {
            return new Blob(this.recordedChunks, { type: this.videoMimeType });
        },
        toggleRecording() {
            if (this.isRecording) {
                this.stopRecording();
            } else {
                this.startRecording();
            }
        },
        discardVideo() {
            this.recordingTime = 0;
            const playbackElement = this.$refs.playback;
            playbackElement.pause();
            URL.revokeObjectURL(playbackElement.src);
            playbackElement.src = '';
            this.isPlayback = false;
            this.recordedChunks = [];
            this.initializeCamera();
        },
        stopStream() {
            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop());
                this.stream = null;
            }
        },
        cleanupRecording() {
            if (this.recordingInterval) {
                clearInterval(this.recordingInterval);
                this.recordingInterval = null;
            }
            if (this.mediaRecorder && this.isRecording) {
                this.mediaRecorder.onstop = null;
                try { this.mediaRecorder.stop(); } catch (e) { this.isRecording = false; }
            }
            this.isRecording = false;
        },
        closeDialog() {
            this.hide();
            this.resetRecording();
        },
        resetRecording() {
            const vm = this;
            vm.cleanupRecording();
            const playbackElement = vm.$refs.playback;
            if (playbackElement) {
                playbackElement.pause();
                URL.revokeObjectURL(playbackElement.src);
                playbackElement.src = '';
            }
            vm.isPlayback = false;
            vm.recordedChunks = [];
            vm.recordingTime = 0;
        },
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        },
        async shareVideo() {
            const vm = this;

            try {
                const videoBlob = vm.recordedBlob();
                const videoName = `${randomString()}_${sanitizeFilename(vm.exercise?.name || 'exercise')}.${mediaExtension(vm.videoMimeType)}`;

                if (vm.isWeb) {
                    const url = URL.createObjectURL(videoBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = videoName
                    link.click();
                    URL.revokeObjectURL(url);
                    this.toast(vm.$t('common.video_downloaded'));
                } else {
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        try {
                            const base64data = reader.result.split(',')[1];

                            const dirResult = await Filesystem.readdir({
                                path: vm.folderPath,
                                directory: Directory.Cache,
                            }).catch(() => null);

                            if (!dirResult) {
                                await Filesystem.mkdir({
                                    path: vm.folderPath,
                                    directory: Directory.Cache,
                                    recursive: true,
                                });
                            }

                            const savedFile = await Filesystem.writeFile({
                                path: `${vm.folderPath}/${videoName}`,
                                data: base64data,
                                directory: Directory.Cache,
                            });

                            await Share.share({
                                title: vm.$t('common.share_video'),
                                text: vm.$t('common.share_video'),
                                files: [savedFile.uri],
                                dialogTitle: vm.$t('common.share_video'),
                            });
                        } catch (err) {
                            console.error(vm.$t('common.error_share'), err);
                            vm.toast(vm.$t('common.error_share'), 'error');
                        }
                    };
                    reader.readAsDataURL(videoBlob);
                }
            } catch (err) {
                console.error(vm.$t('common.error_share'), err);
                this.toast(vm.$t('common.error_share'), 'error');
            }
        },
        async sendToCoach() {
            const vm = this;
            vm.loadingSendToCoach = true
            const { savePath, downloadURL } = await this.uploadVideo(vm.recordedBlob())
            const deleteIn = vm.$moment().add(7, 'days').format('YYYY-MM-DD')
            await db.collection('user_videos').add({
                studentId: vm.studentId,
                coachId: vm.coachId,
                savePath: savePath,
                downloadURL: downloadURL,
                delete_in: deleteIn
            })
            await vm.saveVideoChange(downloadURL, deleteIn)
            await this.sendMessageToCoach(savePath, downloadURL);
            vm.loadingSendToCoach = false
            vm.hide()
            vm.toast(vm.$t('common.video_coach_sent'))
        },
        async saveVideoChange(url, deleteIn) {
            const vm = this
            const block = vm.exercise?.block || null
            if (!vm.assignedRoutineId || !block) return

            await db.collection('assignedRoutines').doc(vm.assignedRoutineId).update({
                'advance.changes': FieldValue.arrayUnion({
                    type: 'video',
                    block: block,
                    exercise: vm.exercise?.name || null,
                    exerciseId: vm.exercise?.objectID || vm.exercise?.id || null,
                    url: url,
                    deleteIn: deleteIn,
                })
            }).catch(() => { })
        },
        async uploadVideo(video) {
            const vm = this;
            const contentType = video.type || DEFAULT_VIDEO_MIME_TYPE
            let uniqueName = `${Date.now()}-${(vm.exercise?.name || 'exercise').replace(/\s+/g, '-')}.${mediaExtension(contentType)}`
            let savePath = `/${vm.studentId}/chats/${vm.coachId}/${uniqueName}`
            let uploadTask = await storageRef.child(savePath).put(video, { contentType })
            let downloadURL = await uploadTask.ref.getDownloadURL()
            return { savePath, downloadURL }
        },
        async sendMessageToCoach(savePath, downloadURL) {
            const vm = this

            await sendMessage({
                message: vm.exercise?.name || '',
                from: vm.studentId,
                type: 'attachment_video',
                file: { savepath: savePath, downloadURL: downloadURL }
            }, vm.coachId, vm.studentId)
        },
    },
};
</script>

<style scoped>
.camera-stage {
    position: relative;
    height: 100vh;
    overflow: hidden;
    background: #000000;
}

.video-fullscreen {
    width: 100vw;
    height: 100vh;
}

.video-fullscreen--preview {
    object-fit: cover;
}

.video-fullscreen--playback {
    object-fit: contain;
}

.glass-btn {
    background: rgba(18, 22, 30, 0.55) !important;
    border: 1px solid rgba(255, 255, 255, 0.16) !important;
    backdrop-filter: blur(10px);
}

.close-button {
    position: fixed;
    top: calc(16px + var(--safe-area-inset-top, 0px));
    right: 16px;
    z-index: 20;
    width: 44px !important;
    height: 44px !important;
}

.recording-time {
    position: fixed;
    top: calc(20px + var(--safe-area-inset-top, 0px));
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #FFFFFF;
    background: rgba(18, 22, 30, 0.55);
    backdrop-filter: blur(10px);
    padding: 6px 14px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
}

.recording-time--warning {
    color: #E85C5C;
}

.recording-progress {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 20;
    height: 3px;
    background: rgba(255, 255, 255, 0.16);
}

.recording-progress-bar {
    display: block;
    height: 100%;
    background: #FFFFFF;
    transition: width 1s linear;
}

.recording-progress-bar--warning {
    background: #E85C5C;
}

.switch-button {
    position: fixed;
    top: calc(16px + var(--safe-area-inset-top, 0px));
    left: 16px;
    z-index: 20;
    width: 44px !important;
    height: 44px !important;
}

.rec-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #E85C5C;
    animation: rec-pulse 1s ease-in-out infinite;
}

@keyframes rec-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.25; }
}

.camera-controls {
    position: fixed;
    bottom: calc(32px + var(--safe-area-inset-bottom, 0px));
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 28px;
}

.shutter {
    width: 76px !important;
    height: 76px !important;
    border-radius: 50% !important;
    border: 4px solid rgba(255, 255, 255, 0.9) !important;
    background: transparent !important;
}

.shutter-core {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--brand-gradient);
    transition: width 0.2s ease, height 0.2s ease, border-radius 0.2s ease, background 0.2s ease;
}

.shutter--recording .shutter-core {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #E85C5C;
}

.action-btn {
    width: 56px !important;
    height: 56px !important;
    border-radius: 50% !important;
}

.action-btn--primary {
    width: 64px !important;
    height: 64px !important;
    background: var(--brand-gradient) !important;
}
</style>
